import { CheckCircleOutlined } from '@ant-design/icons';
import { Flex } from 'antd';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { FormApproveRejectRequest } from '@Types/api/form';
import {
  BSANotification,
  EmailCreationNotification,
  ITAdminNotification,
  PlantEngineerNotification,
  PlantWorkshopManagerNotification,
  PurchaseOfficerNotification,
  RequiredNotification,
  SimAddonNotification,
  SimCardNotification,
} from '@Components/index';
import { ApprovalActions } from '@Enum/index';
import {
  useFormApproveMutation,
  useItRequisitionAprroveMutation,
  useRequisitionAprroveMutation,
} from '@Store/api/approvalsListApiSlice';
import { deciderOfApprovePathName } from '@Utils/deciderOfServicePathName';
import { useAppDispatch, useAppSelector } from '@Hooks/index';
import { stateOpenApproveModal } from '@Store/selectors/approvalsListSelector';
import { approvalsListActions } from '@Store/slices/approvalsListSlice';
import { NotificationContext } from '@Context/NotificationContext';
import {
  useGetExistingPackageMutation,
  useGetITRequestFormQuery,
} from '@Features/EmployeeSelfService/requests/ITRequisition/store/api/itRequisitionApiSlice';
import {
  useGetRequisitionCommodityClassQuery,
  useGetRequisitionQuery,
} from '@Features/MaterialRequest/requests/MaterialRequisition/store/api/MaterialRequestApiSlice';

import { ItRequisitionTypeCodes } from '@Enum/api/ItRequisitionTypeCodes.enum';
import { ItRequisitionStatusCodes } from '@Enum/api/ItRequisitionStatusCodes';
import { RequisitionStatuses } from '@Features/MaterialRequest/enum/RequisitionStatuses.enum';
import { useFormik } from 'formik';

import {
  initBSAFormikValues,
  initITAdmin,
  initNewEmailValues,
  initPlantEngineer,
  initPlantWorkshopManager,
  initPurchaseOfficer,
  initSimAddonFormikValues,
  initSimCardValues,
  SimCardForm,
} from './initFormikValues';

interface ApproveTaskProps {
  taskId: string;
  requestId: number;
  requestNo: string | undefined;
  requestTypeId: string;
  statusId: number | undefined;
  onApprove?: () => void;
}

interface ItRequisitionProcessProps {
  body: FormApproveRejectRequest;
}

interface HRMProcessProps {
  body: FormApproveRejectRequest;
}

const ApproveTask: React.FC<ApproveTaskProps> = ({
  taskId,
  requestNo,
  requestId,
  requestTypeId,
  statusId,
  onApprove,
}) => {
  const [existingPackageValue, setExistingPackageValue] = useState('');
  const { taskApprovedNotification, taskErrorNotification, errorNotification } =
    useContext(NotificationContext);
  const stateOpenModal = useAppSelector(stateOpenApproveModal);
  const dispatch = useAppDispatch();
  const [postApproveRejectForm] = useFormApproveMutation();
  const [getExistingPackage] = useGetExistingPackageMutation();
  const [requisitionApproveRejectForm] = useRequisitionAprroveMutation();
  const [itReqApproveRejectForm] = useItRequisitionAprroveMutation();

  const isMR = requestTypeId === 'Requisition' || requestTypeId === 'MRRequest';

  const { data: mrRequestData } = useGetRequisitionQuery(String(requestId), {
    skip: !isMR,
  });
  const { data: itRequestData } = useGetITRequestFormQuery(String(requestId), {
    skip: requestTypeId !== 'IT Requisition',
  });
  const { data: isShowITAdmin } = useGetRequisitionCommodityClassQuery(
    mrRequestData?.requisitionId || 0,
    {
      skip:
        mrRequestData?.statusId !==
        RequisitionStatuses.AWAITING_APPROVAL_OF_WAREHOUSE_MANAGER,
    }
  );

  const formikBSA = useFormik({
    initialValues: initBSAFormikValues,
    enableReinitialize: true,
    onSubmit: () => undefined,
  });

  const formikSimAddon = useFormik({
    initialValues: initSimAddonFormikValues,
    enableReinitialize: true,
    onSubmit: () => undefined,
  });

  const initialSimCardFormikValue: SimCardForm = useMemo(
    () => ({
      reqCapValue: itRequestData?.reqCapValue || initSimCardValues.reqCapValue,
      mobileNumber:
        itRequestData?.personalMobile || initSimCardValues.mobileNumber,
    }),
    [itRequestData?.personalMobile, itRequestData?.reqCapValue]
  );

  const formikSimCard = useFormik({
    initialValues: initialSimCardFormikValue,
    enableReinitialize: true,
    onSubmit: () => undefined,
  });

  const formikNewEmail = useFormik({
    initialValues: initNewEmailValues,
    enableReinitialize: true,
    onSubmit: () => undefined,
  });

  const formikPurchaseOfficer = useFormik({
    initialValues: initPurchaseOfficer,
    enableReinitialize: true,
    onSubmit: () => undefined,
  });

  const formikPlantEngineer = useFormik({
    initialValues: initPlantEngineer,
    enableReinitialize: true,
    onSubmit: () => undefined,
  });

  const formikPlantWorkshopManager = useFormik({
    initialValues: initPlantWorkshopManager,
    enableReinitialize: true,
    onSubmit: () => undefined,
  });

  const formikITAdmin = useFormik({
    initialValues: initITAdmin,
    enableReinitialize: true,
    onSubmit: () => undefined,
  });

  useEffect(() => {
    if (itRequestData?.requestId) {
      formikNewEmail.setFieldValue('username', itRequestData?.username);
    }
  }, [itRequestData?.requestId]);

  useEffect(() => {
    if (
      itRequestData?.requestId &&
      itRequestData?.reqItemId === ItRequisitionTypeCodes.SIM_ADDON_REQUEST &&
      itRequestData?.statusId === ItRequisitionStatusCodes.AWAITING_APPROVAL
    ) {
      getExistingPackage(itRequestData.requestId)
        .unwrap()
        .then((response) => setExistingPackageValue(response?.packages || ''));
    }
  }, [itRequestData?.requestId]);

  const handleApproveNewEmailModal = useCallback((): void => {
    const { username, password } = formikNewEmail.values;
    const body = {
      taskId: String(taskId),
      command: ApprovalActions.APPROVE,
      comment: '',
      userName: username,
      password,
    };

    itReqApproveRejectForm(body)
      .unwrap()
      .then(() => {
        taskApprovedNotification();
        onApprove?.();
      })
      .catch(() => {
        taskErrorNotification();
      });
  }, [taskId, formikNewEmail.values]);

  const handleApproveBSAModal = useCallback((): void => {
    const { comment, username, password } = formikBSA.values;
    const body = {
      taskId: String(taskId),
      command: ApprovalActions.APPROVE,
      comment,
      userName: username,
      password,
    };

    itReqApproveRejectForm(body)
      .unwrap()
      .then(() => {
        taskApprovedNotification();
        onApprove?.();
      })
      .catch(() => {
        taskErrorNotification();
      });
  }, [taskId, formikBSA.values]);

  const handleApproveSimAddonModal = useCallback((): void => {
    const { recommendPackage } = formikSimAddon.values;
    const body = {
      taskId: String(taskId),
      command: ApprovalActions.APPROVE,
      comment: '',
      existingPackage: existingPackageValue,
      recommendPackage,
    };

    itReqApproveRejectForm(body)
      .unwrap()
      .then(() => {
        taskApprovedNotification();
        onApprove?.();
      })
      .catch(() => {
        taskErrorNotification();
      });
  }, [taskId, existingPackageValue, formikSimAddon.values]);

  const handleApprovePurchaseOfficerModal = useCallback((): void => {
    const { comment, purchaseOfficer } = formikPurchaseOfficer.values;

    if (!purchaseOfficer) {
      errorNotification('Please Choose the Purchase Officer');
      return;
    }

    const body = {
      taskId: String(taskId),
      decision: ApprovalActions.APPROVE,
      comment,
      selectedApproverUserId: Number(purchaseOfficer),
      purchaseOfficerId: Number(purchaseOfficer),
    };

    requisitionApproveRejectForm(body)
      .unwrap()
      .then(() => {
        taskApprovedNotification();
        onApprove?.();
      })
      .catch(() => {
        taskErrorNotification();
      });
  }, [taskId, formikPurchaseOfficer.values]);

  const handleApprovePlantEngineerModal = useCallback((): void => {
    const { comment, plantEngineer } = formikPlantEngineer.values;

    if (!plantEngineer) {
      errorNotification('Please Choose Plant Engineer');
      return;
    }

    const body = {
      taskId: String(taskId),
      decision: ApprovalActions.APPROVE,
      comment,
      selectedApproverUserId: Number(plantEngineer),
    };

    requisitionApproveRejectForm(body)
      .unwrap()
      .then(() => {
        taskApprovedNotification();
        onApprove?.();
      })
      .catch(() => {
        taskErrorNotification();
      });
  }, [taskId, formikPlantEngineer.values]);

  const handleApprovePlantWorkshopManagerModal = useCallback((): void => {
    const { comment, plantWorkshopManager } = formikPlantWorkshopManager.values;

    if (!plantWorkshopManager) {
      errorNotification('Please Choose Plant Workshop Manager');
      return;
    }

    const body = {
      taskId: String(taskId),
      decision: ApprovalActions.APPROVE,
      comment,
      selectedApproverUserId: Number(plantWorkshopManager),
    };

    requisitionApproveRejectForm(body)
      .unwrap()
      .then(() => {
        taskApprovedNotification();
        onApprove?.();
      })
      .catch(() => {
        taskErrorNotification();
      });
  }, [taskId, formikPlantWorkshopManager.values]);

  const handleApproveITAdminModal = useCallback((): void => {
    const { comment, itAdmin } = formikITAdmin.values;

    const body = {
      taskId: String(taskId),
      decision: ApprovalActions.APPROVE,
      comment,
      selectedApproverUserId: Number(itAdmin),
    };

    requisitionApproveRejectForm(body)
      .unwrap()
      .then(() => {
        taskApprovedNotification();
        onApprove?.();
      })
      .catch(() => {
        taskErrorNotification();
      });
  }, [taskId, formikITAdmin.values]);

  const handleApproveSimCardModal = useCallback((): void => {
    const { mobileNumber, reqCapValue } = formikSimCard.values;
    const body = {
      taskId: String(taskId),
      command: ApprovalActions.APPROVE,
      comment: '',
      reqCapValue,
      mobileNumber,
    };

    itReqApproveRejectForm(body)
      .unwrap()
      .then(() => {
        taskApprovedNotification();
        onApprove?.();
      })
      .catch(() => {
        taskErrorNotification();
      });
  }, [taskId, formikSimCard.values]);

  const showModal = (event: React.FormEvent): void => {
    const target = event.target as HTMLInputElement;
    const buttonId = (event.target as HTMLButtonElement)?.id;
    const targetText = target?.textContent;

    if (
      buttonId !== 'cancel' &&
      targetText !== 'No, Thanks' &&
      targetText !== 'Yes, Approve'
    ) {
      dispatch(approvalsListActions.setOpenModalApprove(true));
    }
  };

  const servicePathName = useMemo(
    () => deciderOfApprovePathName(requestTypeId),
    [requestTypeId]
  );

  const onItRequisitionProcess = ({
    body,
  }: ItRequisitionProcessProps): void => {
    itReqApproveRejectForm({
      ...body,
    })
      .unwrap()
      .then(() => {
        taskApprovedNotification();
        onApprove?.();
      })
      .catch(() => {
        taskErrorNotification();
      });
  };

  const onRequisitionProcess = (): void => {
    requisitionApproveRejectForm({
      comment: '',
      decision: ApprovalActions.APPROVE,
      selectedApproverUserId: 0,
      taskId: String(taskId),
    })
      .unwrap()
      .then(() => {
        taskApprovedNotification();
        onApprove?.();
      })
      .catch(() => {
        taskErrorNotification();
      });
  };

  const onHRMProcess = ({ body }: HRMProcessProps): void => {
    postApproveRejectForm({
      servicePathName,
      body,
    })
      .unwrap()
      .then(() => {
        taskApprovedNotification();
        onApprove?.();
      })
      .catch(() => {
        taskErrorNotification();
      });
  };

  const isNewEmailRequest = useMemo(() => {
    if (
      itRequestData?.reqItemId === ItRequisitionTypeCodes.EMAIL &&
      itRequestData?.statusId ===
        ItRequisitionStatusCodes.AWAITING_FOR_EMAIL_CREATION
    ) {
      return true;
    }

    return false;
  }, [itRequestData?.reqItemId, itRequestData?.statusId]);

  const isPurchaseOfficerRequest = useMemo(() => {
    if (
      mrRequestData?.statusId ===
      RequisitionStatuses.AWAITING_APPROVAL_OF_PROCUREMENT_MANAGER
    ) {
      return true;
    }
    return false;
  }, [mrRequestData]);

  const isPlantEngineerRequest = useMemo(() => {
    if (
      mrRequestData?.statusId ===
      RequisitionStatuses.AWAITING_APPROVAL_OF_SENIOR_STORE_KEEPER
    ) {
      return true;
    }

    return false;
  }, [mrRequestData?.statusId]);

  const isPlantWorkshopManagerRequest = useMemo(() => {
    if (
      mrRequestData?.statusId ===
      RequisitionStatuses.AWAITING_APPROVAL_OF_PLANT_ENGINEER
    ) {
      return true;
    }

    return false;
  }, [mrRequestData?.statusId]);

  const isITAdminRequest = useMemo(() => {
    if (
      mrRequestData?.statusId ===
        RequisitionStatuses.AWAITING_APPROVAL_OF_WAREHOUSE_MANAGER &&
      isShowITAdmin
    ) {
      return true;
    }

    return false;
  }, [mrRequestData?.statusId, isShowITAdmin]);

  const isSimCardRequest = useMemo(() => {
    if (itRequestData?.reqItemId === ItRequisitionTypeCodes.NEW_SIM_CARD) {
      if (
        itRequestData?.statusId ===
        ItRequisitionStatusCodes.AWAITING_SIM_CARD_FROM_ETISALAT
      ) {
        return 'Input';
      }

      if (
        itRequestData?.statusId ===
          ItRequisitionStatusCodes.AWAITING_APPROVAL_OF_ADMIN_MANAGER ||
        itRequestData?.statusId ===
          ItRequisitionStatusCodes.AWAITING_APPROVAL_OF_PAYROLL_ADMIN
      ) {
        return 'Dropdown';
      }
    }
    return '';
  }, [itRequestData?.reqItemId, itRequestData?.statusId]);

  const isSimAddonRequest = useMemo(() => {
    if (
      itRequestData?.reqItemId === ItRequisitionTypeCodes.SIM_ADDON_REQUEST &&
      itRequestData?.statusId === ItRequisitionStatusCodes.AWAITING_APPROVAL &&
      !itRequestData?.recommendPackage
    ) {
      return true;
    }

    return false;
  }, [itRequestData?.reqItemId, itRequestData?.statusId]);

  const isBSARequest = useMemo(() => {
    if (
      itRequestData?.reqItemId === ItRequisitionTypeCodes.BUILD_SMART_ACCESS &&
      itRequestData?.statusId === ItRequisitionStatusCodes.UNDER_PROCESS
    ) {
      return true;
    }

    return false;
  }, [itRequestData?.statusId, itRequestData?.reqItemId]);

  const handleOk = useCallback((): void => {
    const body: FormApproveRejectRequest = {
      taskId: String(taskId),
      command: ApprovalActions.APPROVE,
      comment: '',
    };

    if (requestTypeId === 'IT Requisition') {
      onItRequisitionProcess({
        body,
      });
    }

    if (isMR) {
      onRequisitionProcess();
    }

    if (!isMR && requestTypeId !== 'IT Requisition') {
      onHRMProcess({ body });
    }

    dispatch(approvalsListActions.setOpenModalApprove(false));
  }, [
    itRequestData,
    mrRequestData,
    taskId,
    requestTypeId,
    statusId,
    existingPackageValue,
  ]);

  const handleCancel = (): void => {
    dispatch(approvalsListActions.setOpenModalApprove(false));
  };

  const message = useMemo(
    () => `It seems like you're about to approve
  request ${isMR ? requestNo : requestId}. Are you sure you want to proceed with this action?`,
    [requestId, requestNo, isMR]
  );

  const textSuccessBtn = useMemo(() => {
    if (
      mrRequestData?.statusId ===
      RequisitionStatuses.UNDER_PROCESS_WITH_PURCHASE
    ) {
      return 'Complete';
    }

    return 'Yes, Approve';
  }, [mrRequestData?.statusId]);

  return (
    <Flex className="trojan-action-space" gap={15} onClick={showModal}>
      <CheckCircleOutlined /> <span>Approve</span>
      {isNewEmailRequest && (
        <EmailCreationNotification
          title="Approve Request"
          isModalOpen={stateOpenModal}
          textSuccessBtn="Yes, Approve"
          handleCancel={handleCancel}
          handleSuccess={handleApproveNewEmailModal}
          formik={formikNewEmail}
        />
      )}
      {isSimCardRequest && (
        <SimCardNotification
          title="Approve Request"
          isModalOpen={stateOpenModal}
          textSuccessBtn="Yes, Approve"
          handleCancel={handleCancel}
          handleSuccess={handleApproveSimCardModal}
          formik={formikSimCard}
          simCardRequest={isSimCardRequest}
        />
      )}
      {isSimAddonRequest && (
        <SimAddonNotification
          title="Approve Request"
          isModalOpen={stateOpenModal}
          textSuccessBtn="Yes, Approve"
          handleCancel={handleCancel}
          handleSuccess={handleApproveSimAddonModal}
          formik={formikSimAddon}
          existingPackage={existingPackageValue}
        />
      )}
      {isBSARequest && (
        <BSANotification
          title="Approve Request"
          isModalOpen={stateOpenModal}
          textSuccessBtn="Yes, Approve"
          handleCancel={handleCancel}
          handleSuccess={handleApproveBSAModal}
          formik={formikBSA}
        />
      )}
      {isPurchaseOfficerRequest && (
        <PurchaseOfficerNotification
          title="Approve Request"
          isModalOpen={stateOpenModal}
          textSuccessBtn="Yes, Approve"
          handleCancel={handleCancel}
          handleSuccess={handleApprovePurchaseOfficerModal}
          formik={formikPurchaseOfficer}
        />
      )}
      {isPlantEngineerRequest && (
        <PlantEngineerNotification
          title="Approve Request"
          message={message}
          isModalOpen={stateOpenModal}
          textSuccessBtn="Yes, Approve"
          handleCancel={handleCancel}
          handleSuccess={handleApprovePlantEngineerModal}
          formik={formikPlantEngineer}
        />
      )}
      {isPlantWorkshopManagerRequest && (
        <PlantWorkshopManagerNotification
          title="Approve Request"
          message={message}
          isModalOpen={stateOpenModal}
          textSuccessBtn="Yes, Approve"
          handleCancel={handleCancel}
          handleSuccess={handleApprovePlantWorkshopManagerModal}
          formik={formikPlantWorkshopManager}
        />
      )}
      {isITAdminRequest && mrRequestData?.compId && (
        <ITAdminNotification
          title="Approve Request"
          message={message}
          isModalOpen={stateOpenModal}
          textSuccessBtn="Yes, Approve"
          handleCancel={handleCancel}
          handleSuccess={handleApproveITAdminModal}
          formik={formikITAdmin}
          compId={mrRequestData?.compId}
        />
      )}
      {!isBSARequest &&
        !isSimAddonRequest &&
        !isPurchaseOfficerRequest &&
        !isPlantEngineerRequest &&
        !isPlantWorkshopManagerRequest &&
        !isITAdminRequest &&
        !isSimCardRequest &&
        !isNewEmailRequest && (
          <RequiredNotification
            title="Approve Request"
            message={message}
            isModalOpen={stateOpenModal}
            textSuccessBtn={textSuccessBtn}
            isSuccessIcon
            handleCancel={handleCancel}
            handleSuccess={handleOk}
          />
        )}
    </Flex>
  );
};

export default ApproveTask;
